import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { PaymentSheet, PaymentSheetListObj } from '../../../models/PaymentSheet';
import { PaymentSheetService } from '../../../services/PaymentSheet.service';
import { hideSpinner, showSpinner } from '../../../store/slicers/globalSpinner.slicer';
import { showSnackbarAlert } from '../../../store/slicers/snackbarAlert.slicer';
import { checkResponseStatus } from '../../../utils/api/response';
import { formatCurrency } from '../../../utils/utils';
import styles from './styles.module.css';

interface Props {
  item: PaymentSheetListObj;
  onClose: () => void;
}

export const PaymentSheetsDetailsDialog = ({ item, onClose }: Props) => {
  const theme = useTheme();
  const componentRef = useRef();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [paymentSheetDetails, setPaymentSheetDetails] = useState<PaymentSheet>({} as PaymentSheet);
  const PAYMENT_SHEET_DATE =
    (paymentSheetDetails?.dados_data_pgto?.datapagamento &&
      paymentSheetDetails?.dados_data_pgto?.datapagamento.toString().split(' ')[0].replaceAll('-', '/')) ||
    '';

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Folha de Pagamento - ${PAYMENT_SHEET_DATE}`
  });

  const formatAgencyName = (agency: string) => (agency?.toLowerCase().startsWith('ag.') ? agency?.slice(3) : agency);

  const fetchPaymentSheetDetails = async (item) => {
    dispatch(showSpinner());
    try {
      const response = await PaymentSheetService.getPaymentSheet(item.codigofolha, item.datafolha);
      if (response && checkResponseStatus(response)) {
        setPaymentSheetDetails(response.data);
      }
    } catch (error) {
      dispatch(showSnackbarAlert({ message: 'Erro ao buscar detalhes da folha de pagamento', severity: 'error' }));
    } finally {
      dispatch(hideSpinner());
    }
  };

  useEffect(() => {
    fetchPaymentSheetDetails(item);
  }, [item]);

  if (!Object.keys(paymentSheetDetails).length) return null;

  const calculatePadding = (itemsCount: number) => {
    const totalItems = 10;
    const basePadding = 100; // base padding in px
    const rowHeight = 20; // height of each row in px

    const missingItems = totalItems - itemsCount;
    return basePadding + missingItems * rowHeight;
  };

  const itemsCount = paymentSheetDetails?.dados_vencimento?.length || 0;
  const lastItemPadding = calculatePadding(itemsCount);

  return (
    <Dialog open={!!item} onClose={onClose} aria-labelledby="responsive-dialog-title" fullWidth fullScreen={isMobile}>
      <DialogTitle variant="h4" color="primary">
        Detalhes da folha
      </DialogTitle>
      <Divider />
      <DialogContent ref={componentRef} sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto' }}>
        <table className={styles.table}>
          <thead>
            <tr className={`${styles['border-bottom']} ${styles['border-right']}`}>
              <th colSpan={3} className={styles['border-right']}>
                <Typography variant="h5" textAlign="left">
                  Demonstrativo de Pagamento
                </Typography>
              </th>
              <th colSpan={2}>
                <Typography variant="h5" textAlign="left">
                  {paymentSheetDetails.dados_titulo_folha}
                </Typography>
              </th>
            </tr>
            <tr className={styles['border-bottom']}>
              <td colSpan={3} className={styles['border-right']}>
                Resfri Ar Climatizadores e Equipamentos
              </td>
              <td colSpan={2} className={styles['text-uppercase']}>
                CNPJ: 01.986.608/0001-08
              </td>
            </tr>
            <tr className={styles['border-bottom']} style={{ backgroundColor: theme.palette.grey[100] }}>
              <td colSpan={3} className={styles['border-right']}>
                <Typography variant="subtitle1">{paymentSheetDetails.dados_contrato.nome}</Typography>
              </td>
              <td colSpan={1} className={styles['border-right']}>
                <Typography variant="subtitle1">
                  {item.unidade}/{item.contrato}
                </Typography>
              </td>
              <td colSpan={1} style={{ maxWidth: '180px' }}>
                <Typography variant="subtitle1" sx={{ overflow: 'hidden' }}>
                  {paymentSheetDetails?.dados_cargo?.descricaocargo?.toString()}
                </Typography>
              </td>
            </tr>
            <tr>
              <td className={`${styles['border-right']} ${styles.underline}`}>Cod.</td>
              <td className={`${styles['border-right']} ${styles.underline}`}>Descrição</td>
              <td className={`${styles['border-right']} ${styles['text-value']} ${styles.underline}`}>Hrs/Qtde</td>
              <td className={`${styles['border-right']} ${styles['text-value']} ${styles.underline}`}>Vencimentos</td>
              <td className={`${styles['border-right']} ${styles['text-value']} ${styles.underline}`}>Descontos</td>
            </tr>
          </thead>
          <tbody className={styles['border-bottom']}>
            {paymentSheetDetails?.dados_vencimento?.map((vencimento, index) => {
              const isLastRow = index === itemsCount - 1;
              const cellStyle = isLastRow ? { paddingBottom: `${lastItemPadding}px` } : {};

              return (
                <tr key={index} className={styles.trBody}>
                  <td className={styles['border-right']} style={cellStyle}>
                    {vencimento?.vdb}
                  </td>
                  <td className={styles['border-right']} style={cellStyle}>
                    {vencimento?.descricao15}
                  </td>
                  <td className={`${styles['border-right']} ${styles['text-value']}`} style={cellStyle}>
                    {vencimento.horasquantidade
                      ? Number(vencimento.horasquantidade).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })
                      : ''}
                  </td>
                  {vencimento.tipovdb === 'V' && (
                    <>
                      <td className={`${styles['border-right']} ${styles['text-value']}`} style={cellStyle}>
                        {formatCurrency(vencimento.valor, false)}
                      </td>
                      <td className={`${styles['border-right']} ${styles['text-value']}`}></td>
                    </>
                  )}
                  {vencimento.tipovdb === 'D' && (
                    <>
                      <td className={`${styles['border-right']} ${styles['text-value']}`}></td>
                      <td className={`${styles['border-right']} ${styles['text-value']}`} style={cellStyle}>
                        {formatCurrency(vencimento.valor, false)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className={`${styles['border-bottom']} ${styles['border-right']}`}>
                <table className={styles['table-clean']} style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td className={`${styles['border-right']} ${styles['text-value']}`}>
                        <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Base INSS</span>
                        <span className={styles['row-label-value']}>
                          {paymentSheetDetails?.dados_salario_contratual?.salariocontratual
                            ? formatCurrency(paymentSheetDetails?.dados_salario_contratual?.salariocontratual, false)
                            : '0,00'}
                        </span>
                      </td>
                      <td className={`${styles['border-right']} ${styles['text-value']}`}>
                        <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Base INSS</span>
                        <span className={styles['row-label-value']}>
                          {paymentSheetDetails?.dados_rodape?.Base_INSS_sobre_a_Folha?.valor
                            ? formatCurrency(paymentSheetDetails?.dados_rodape?.Base_INSS_sobre_a_Folha?.valor, false)
                            : '0,00'}
                        </span>
                      </td>
                      <td className={`${styles['border-right']} ${styles['text-value']}`}>
                        <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Base IRRF</span>
                        <span className={styles['row-label-value']}>
                          {paymentSheetDetails?.dados_rodape?.Base_IRF_sobre_a_Folha?.valor
                            ? formatCurrency(paymentSheetDetails?.dados_rodape?.Base_IRF_sobre_a_Folha?.valor, false)
                            : '0,00'}
                        </span>
                      </td>
                      <td className={`${styles['text-value']}`}>
                        <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Base IRRF</span>
                        <span className={styles['row-label-value']}>
                          {paymentSheetDetails?.dados_fgts?.base ? formatCurrency(paymentSheetDetails.dados_fgts.base, false) : '0,00'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td className={`${styles['border-bottom']} ${styles['border-right']} ${styles['text-value']}`}>
                <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Total de Vencimentos</span>
                <span className={styles['row-label-value']}>
                  {paymentSheetDetails?.dados_rodape?.Total_dos_Vencimentos?.valor
                    ? formatCurrency(paymentSheetDetails?.dados_rodape?.Total_dos_Vencimentos?.valor, false)
                    : '0,00'}
                </span>
              </td>
              <td className={`${styles['border-bottom']} ${styles['border-right']} ${styles['text-value']}`}>
                <span className={`${styles['table-label-title']} ${styles['display-block']}`}>Total de Descontos</span>
                <span className={styles['row-label-value']}>
                  {paymentSheetDetails?.dados_rodape?.Total_dos_Descontos?.valor
                    ? formatCurrency(paymentSheetDetails?.dados_rodape?.Total_dos_Descontos?.valor, false)
                    : '0,00'}
                </span>
              </td>
            </tr>
            <tr>
              <td className={`${styles['border-bottom']} ${styles['border-right']} ${styles['text-value']}`} colSpan={4}>
                <Typography textAlign="left">
                  Depositado na Conta: {paymentSheetDetails.dados_rodape.dados_bancarios.contacorrente} <br />
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Banco: {paymentSheetDetails.dados_rodape.dados_bancarios.descricao40} </Typography>
                  <Typography>Agência: {formatAgencyName(paymentSheetDetails.dados_rodape.dados_bancarios.agencia)} </Typography>
                </Stack>
              </td>
              <td
                className={`${styles['border-bottom']} ${styles['border-right']} ${styles['text-value']} ${styles['row-label-value']}`}
                style={{ backgroundColor: '#f1f1f1' }}
              >
                <Typography variant="subtitle1">Líquido:</Typography>
                <Typography variant="subtitle1">
                  {paymentSheetDetails?.dados_soma_liq?.base ? formatCurrency(paymentSheetDetails.dados_soma_liq.base, false) : '0,00'}
                </Typography>
              </td>
            </tr>
            <tr>
              <td colSpan={5}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" columnGap={8}>
                  <Typography>
                    Recebi o valor líquido deste recibo, correspondente à discriminação acima do qual dou plena e total quitação.
                  </Typography>
                  <Stack alignItems="center">
                    <Typography>{PAYMENT_SHEET_DATE}</Typography>
                  </Stack>
                  <Stack alignItems="center">
                    <Typography>_____________________________________</Typography>
                    <Typography>Assinatura do funcionário</Typography>
                  </Stack>
                </Stack>
              </td>
            </tr>
          </tfoot>
        </table>
      </DialogContent>
      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button startIcon={<CloseIcon />} variant="outlined" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={handlePrint}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};
